import './OccupancyRate.css'
import { Helmet } from 'react-helmet-async';
import ArrowRight from '../../icons/ArrowRight';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useLanguagePrefix from '../../LanguagePrefix';
import ToolFixedMenu from './ToolFixedMenu';


function OccupancyRate() {
    const langPrefix = useLanguagePrefix();
    const [roomOccupied, setRoomOccupied] = useState(null);
    const [roomAvailable, setRoomAvailable] = useState(null);
    const [occupancyRate, setOccupancyRate] = useState(0);
    const { t } = useTranslation();


    const handleInputChange = (e, type) => {
        const value = e.target.value;
        if (type === 'occupied') {
            setRoomOccupied(value);
        } else if (type === 'available') {
            setRoomAvailable(value);
        }
    };


    const calculatedOccupancyRate = () => {
        if (roomOccupied > 0 && roomAvailable > 0) {
            const result = (roomOccupied / roomAvailable) * 100;
            const formatted = Number.isInteger(result) ? result : result.toFixed(2);
            setOccupancyRate(formatted);
        } else {
            setOccupancyRate(null);
        }
    };

    return (
        <div>
            <Helmet>


                <title>Occupancy Rate Calculator – Hotel Room Occupancy Formula - Prostay</title>
                <meta name="description" content="Quickly calculate your hotel’s occupancy rate using Prostay’s free tool. Learn the occupancy formula, how to use it, and why it matters for maximizing room revenue." />
                <link rel="canonical" href={"https://www.prostay.com" + langPrefix + "/occupancy-rate/"} />

                <link rel="alternate" href="https://www.prostay.com/en/occupancy-rate/" />
                <link rel="alternate" href="https://www.prostay.com/es/occupancy-rate/" />

                <meta property="og:title" content="Occupancy Rate Calculator – Hotel Room Occupancy Formula - Prostay" />
                <meta property="og:description" content="Quickly calculate your hotel’s occupancy rate using Prostay’s free tool. Learn the occupancy formula, how to use it, and why it matters for maximizing room revenue." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={"https://www.prostay.com" + langPrefix + "/occupancy-rate/"} />
                <meta property="og:image" content='https://prostay.com/images/ogImage.png' />

                <meta property="og:site_name" content="Prostay" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Occupancy Rate Calculator – Hotel Room Occupancy Formula - Prostay" />
                <meta name="twitter:description" content="Quickly calculate your hotel’s occupancy rate using Prostay’s free tool. Learn the occupancy formula, how to use it, and why it matters for maximizing room revenue." />
                <meta name="twitter:image" content='https://prostay.com/images/ogImage.png' />
                <meta name="twitter:site" content="@prostaypms" />
                <meta name="twitter:creator" content="@prostaypms" />
            </Helmet>
            <div className='d-flex occupancy-rate flex-column-reverse flex-xl-row'>
                <div className='d-flex flex-column width-left gap-4'>
                    <div>
                        <h1 style={{ margin: '0px' }}>
                            {t('Occupancy Rate Calculator – Hotel Room Utilization Tool')}
                        </h1>
                        <h6 className='mt-4'>
                            {t('Easily Calculate Your Hotel’s Occupancy Rate.')} <br />
                            {t('Use this free occupancy rate calculator to understand how effectively your rooms are being filled and make better revenue decisions.')}
                        </h6>
                        <div className='d-flex flex-column-reverse flex-xl-row gap-2 gap-sm-5 mt-5'>
                            <div className='d-flex flex-column gap-4'>
                                <div className='d-flex flex-column mt-4'>
                                    <label>
                                        {t('Total Rooms Occupied')}
                                    </label>
                                    <input
                                        type='number'
                                        value={roomOccupied}
                                        onChange={(e) => handleInputChange(e, 'occupied')}
                                        placeholder={t('Enter amount here')} />
                                </div>
                                <div className='d-flex flex-column mt-4'>
                                    <label>
                                        {t('Total Room Available')}
                                    </label>
                                    <input
                                        type='number'
                                        value={roomAvailable}
                                        onChange={(e) => handleInputChange(e, 'available')}
                                        placeholder={t('Enter number here')} />
                                </div>
                                <div className='d-flex gap-3 gap-sm-5 flex-column flex-sm-row align-items-center'>
                                    <button className='light-blue-filled-btn-xsmall' style={{ height: '38px', width: '111px' }} onClick={calculatedOccupancyRate}>
                                        {t('Calculate')}
                                        <ArrowRight />
                                    </button>
                                    <div className='d-flex flex-column w-100'>
                                        <label>
                                            {t('Occupancy Rate')}
                                        </label>
                                        <input
                                            readOnly
                                            value={`${occupancyRate}%`}
                                            type='text'
                                            placeholder='0%' />
                                    </div>
                                </div>
                            </div>
                            <div className='center-div'>
                                <h2>
                                    {t('What is Occupancy Rate?')}
                                </h2>
                                <p className='mt-3'>
                                    {t('Occupancy rate is one of the most fundamental metrics in hotel operations. It shows what percentage of your available rooms are being sold during a specific time period.')}
                                </p>
                                <br />
                                <p>
                                    {t('By revealing how full your hotel is, this metric helps you understand demand patterns, evaluate your pricing strategy, and plan for staffing, inventory, and marketing.')}
                                </p>
                                <br />
                                <p>
                                    {t('Whether you’re running a boutique hotel or a large resort, tracking occupancy consistently allows you to make informed decisions that affect both daily operations and long-term performance.')}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className='gap-3 d-flex flex-column mt-3'>
                        <h2>
                            {t('Occupancy Rate Formula')}
                        </h2>
                        <p>
                            {t('The formula to calculate occupancy rate is:')}
                        </p>
                        <div className='d-flex align-items-center formula gap-2'>
                            <div className='d-flex align-items-center gap-2'>
                                <p>
                                    {t('Occupancy Rate')}  =
                                </p>
                                <div className='d-flex flex-column align-items-center'>
                                    <p>
                                        {t('Sold Rooms')}
                                    </p>
                                    <p style={{ borderTop: '0.5px solid #1643E0' }}>
                                        {t('Available Rooms')}
                                    </p>
                                </div>
                            </div>
                            <p style={{ paddingInline: '5px' }}>
                                x 100
                            </p>
                        </div>
                        <p>
                            {t('To use it, divide the number of rooms you sold during a specific period by the total number of rooms available, then multiply the result by 100 to convert it into a percentage. For example, if you sold 75 rooms out of 100 available, your occupancy rate would be 75%.')}
                        </p>
                        <p>
                            {t('This calculation provides a fast, reliable way to measure how effectively your hotel is utilizing its available room inventory. It’s typically tracked daily, weekly, and monthly as part of standard performance reporting.')}
                        </p>
                    </div>
                    <div className='d-flex flex-column gap-3'>
                        <h2>
                            {t('Why Occupancy Rate Is Essential for Hotel Success')}
                        </h2>
                        <p>
                            {t('Occupancy rate is more than just a number — it’s a direct reflection of your hotel’s ability to attract and convert bookings. High occupancy can indicate strong demand or successful marketing, while a low rate might point to pricing issues, distribution gaps, or competitive pressure.')}
                        </p>
                        <p>
                            {t('When used alongside other metrics like ADR and RevPAR, occupancy helps paint a complete picture of your hotel’s performance. It also plays a crucial role in budgeting, forecasting, and scheduling.')}
                        </p>
                        <p>
                            {t('For instance, understanding expected occupancy helps you plan housekeeping shifts, control operating costs, and align staff resources more efficiently.')}
                        </p>
                    </div>
                    <div className='d-flex flex-column gap-3 mt-2'>
                        <h2>
                            {t('How Occupancy Rate Works with ADR and RevPAR')}
                        </h2>
                        <p>
                            {t('Occupancy alone doesn’t tell you how much revenue you’re generating — that’s where it works together with ADR and RevPAR. While occupancy tells you how many rooms are sold, ')} <a href={`${langPrefix}/adr-calculator/`}>{t('ADR')}</a> {t(' tells you at what average rate they were sold.')}
                        </p>
                        <p>
                            {t('RevPAR combines both into a single metric that shows how well your inventory is being monetized. Tracking all three gives you a well-rounded view of your performance.')}
                        </p>
                        <p>
                            {t('For example, a high occupancy rate with a low ADR may mean you’re filling rooms but leaving revenue on the table. A lower occupancy rate with a high ADR might show strong pricing power but limited demand. Understanding these dynamics helps you find the right balance between rate and volume.')}
                        </p>
                    </div>
                </div>
                <ToolFixedMenu />
            </div>
        </div>
    )
}

export default OccupancyRate;